import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Eye, Trash2, Plus, AlertTriangle, Shield, AlertOctagon, Bell, BellOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: "bg-emerald-100", text: "text-emerald-700", icon: Shield },
    medium: { bg: "bg-amber-100", text: "text-amber-700", icon: AlertTriangle },
    high: { bg: "bg-rose-100", text: "text-rose-700", icon: AlertOctagon }
  };
  const c = config[level] || config.low;
  const Icon = c.icon;
  return (
    <Badge className={`${c.bg} ${c.text} border-0`}>
      <Icon className="w-3 h-3 mr-1" />
      {level === 'low' ? 'Низкий' : level === 'medium' ? 'Средний' : 'Высокий'}
    </Badge>
  );
};

const AdminWatchlist = ({ user }) => {
  const [watchlist, setWatchlist] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [label, setLabel] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [watchRes, addrRes] = await Promise.all([
        axios.get(`${API}/admin/watchlist`, { withCredentials: true }),
        axios.get(`${API}/admin/addresses`, { withCredentials: true })
      ]);
      setWatchlist(watchRes.data);
      setAddresses(addrRes.data);
    } catch (error) {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedAddress) {
      toast.error("Выберите адрес");
      return;
    }
    try {
      await axios.post(`${API}/admin/watchlist`, {
        address_id: selectedAddress,
        label: label,
        alert_on_high_risk: true,
        alert_on_new_tx: false
      }, { withCredentials: true });
      toast.success("Добавлено в watchlist");
      setAddDialog(false);
      setSelectedAddress("");
      setLabel("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка");
    }
  };

  const handleRemove = async (addressId) => {
    try {
      await axios.delete(`${API}/admin/watchlist/${addressId}`, { withCredentials: true });
      toast.success("Удалено из watchlist");
      fetchData();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  // Filter addresses not already in watchlist
  const availableAddresses = addresses.filter(
    a => !watchlist.some(w => w.address_id === a.address_id)
  );

  if (loading) {
    return (
      <AdminLayout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout user={user}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Watchlist</h1>
            <p className="text-slate-500">Отслеживаемые адреса</p>
          </div>
          <Button onClick={() => setAddDialog(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />
            Добавить
          </Button>
        </div>

        {watchlist.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Watchlist пуст</p>
              <p className="text-sm text-slate-400">Добавьте адреса для отслеживания</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {watchlist.map((item) => {
              const addr = item.address_data || {};
              return (
                <Card key={item.address_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <RiskBadge level={addr.risk_level} />
                          {item.alert_on_high_risk && (
                            <Badge variant="outline" className="text-xs">
                              <Bell className="w-3 h-3 mr-1" />
                              Оповещения
                            </Badge>
                          )}
                        </div>
                        <p className="font-mono text-sm text-slate-900 truncate">
                          {addr.address}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span>Баланс: ${addr.total_balance?.toLocaleString() || 0}</span>
                          <span>Доверие: {addr.trust_score || 0}/100</span>
                          {item.label && <span className="text-slate-400">{item.label}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/address/${item.address_id}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemove(item.address_id)}
                          className="text-rose-600 hover:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add Dialog */}
        <Dialog open={addDialog} onOpenChange={setAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Добавить в Watchlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Адрес</Label>
                <Select value={selectedAddress} onValueChange={setSelectedAddress}>
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите адрес" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAddresses.map(a => (
                      <SelectItem key={a.address_id} value={a.address_id}>
                        {a.address.slice(0, 20)}... ({a.label || 'Без метки'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Метка (опционально)</Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Например: Подозрительный"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialog(false)}>
                Отмена
              </Button>
              <Button onClick={handleAdd} className="bg-slate-900 hover:bg-slate-800">
                Добавить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminWatchlist;
